import { useEffect, useState } from "react";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { supabase } from "@/integrations/supabase/client";
import { SAMPLE_OUTFIT_ID } from "@/lib/sampleOutfit";

const INK_BG = "#2C2820";
const CORAL = "#C84B2F";
const CREAM = "#FFFCF8";
const MUTED_CREAM = "#FAECE7";

interface HomeDemoCardProps {
  onTap: () => void;
}

// HOME-DEMO-CARD-1 — Guest-only inverted-ink card that invites first-time
// visitors to try the canonical sample outfit. Reuses get_sample_outfit RPC
// for thumbnail; tap handler delegates to lifted handleTrySample with
// source="home_demo_card".
export default function HomeDemoCard({ onTap }: HomeDemoCardProps) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any).rpc("get_sample_outfit", {
          p_id: SAMPLE_OUTFIT_ID,
        });
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        const url = row.lookbook_image_url ?? row.image_url ?? null;
        if (url) setThumbUrl(url as string);
      } catch {
        // Silent — card still renders without thumbnail.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = () => {
    void triggerHaptic("primary-action");
    onTap();
  };

  return (
    <div style={{ padding: "0 16px", marginBottom: 12 }}>
      <button
        type="button"
        onClick={handleClick}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          background: INK_BG,
          border: "none",
          borderRadius: 20,
          padding: 18,
          cursor: "pointer",
        }}
      >
        <div
          style={{
            color: CORAL,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: 11,
            fontWeight: 500,
            marginBottom: 6,
          }}
        >
          See it in action
        </div>
        <div
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontStyle: "italic",
            fontSize: 17,
            fontWeight: 400,
            color: CREAM,
            lineHeight: 1.2,
            marginBottom: 6,
          }}
        >
          Try with an example look
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: MUTED_CREAM,
            opacity: 0.8,
            lineHeight: 1.4,
            marginBottom: 14,
          }}
        >
          See how Like2Fit works with a sample outfit. No signup, no paste.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 48,
              height: 64,
              borderRadius: 8,
              flexShrink: 0,
              background: thumbUrl
                ? `#3a342c url("${thumbUrl}") center/cover no-repeat`
                : "#3a342c",
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: CREAM }}>
              Tap to see a verdict
            </div>
            <div
              style={{
                fontSize: 11,
                color: MUTED_CREAM,
                opacity: 0.7,
                marginTop: 2,
              }}
            >
              Sample outfit
            </div>
          </div>
          <span
            style={{
              background: CORAL,
              color: CREAM,
              padding: "7px 13px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Try it
          </span>
        </div>
      </button>
    </div>
  );
}
